import type { Step } from '@/components/GuideMe'
import { isOpenBankingEligibleIncome } from '@/components/Requests/incomeEligibility'
import type { ChaseType, Income, IncomeType } from '@/types/Activity'
import type { OutcomeStatus } from '@/types/Outcome'
import { isFinalOutcome } from '@/utils/outcome'

type IncomeStatus = 'final' | 'advanced' | 'default'

export const shouldShowEstimatedGrossIncome = (
  incomeType: IncomeType,
  hasSingleIncome: boolean,
  estimatedNetIncome: number,
  estimatedGrossIncome: number,
) =>
  incomeType.toLowerCase() === 'employment' &&
  hasSingleIncome &&
  estimatedGrossIncome >= estimatedNetIncome

export const getGuideMeIncomeData = (
  incomeOutcomeStatus?: OutcomeStatus,
  chaseType?: ChaseType,
): Step[] => {
  const status = getIncomeStatus(incomeOutcomeStatus, chaseType)

  switch (status) {
    case 'final':
      return [
        {
          title: 'Nothing left to do for income 🎉',
          description: 'This section now has an outcome and is closed.',
          supportingDocuments: null,
          callToAction: null,
        },
      ]
    case 'advanced':
      return [
        {
          title: 'Chase cycle',
          description:
            'Daily calls should be placed from day 2 of the chase cycle to get in contact with the referee and/or applicant. Applicants should be called if we do not get through to the referee but we are not required to call them if we have spoken to the referee first- this is so that we can ensure we have progressed the application. \n \nAll documentation should be checked, assessed and noted in the platform: however not completing on them until after day 3 of the chase cycle. This is to leave a window for the employer to complete and return a 3rd party reference as this tier prioritizes references; if the agent has chosen this tier it is in part because they would rather a reference than docs.',
          supportingDocuments: null,
          callToAction: null,
        },
      ]
    default:
      return []
  }
}

export const getIncomeStatus = (
  incomeOutcomeStatus?: OutcomeStatus,
  chaseType?: string,
): IncomeStatus => {
  if (isFinalOutcome(incomeOutcomeStatus)) return 'final'
  if (chaseType === 'plus') return 'advanced'
  return 'default'
}

export const getOpenBankingOptOutReasonText = (category: string): string => {
  const reasonMapping: Record<string, string> = {
    bankNotListed: "My bank isn't listed",
    connectionFailed: "I tried to connect but it didn't work",
    privacyConcerns: "I'm worried about sharing my bank information",
    incomeAccuracy: "This won't show my income correctly",
    somethingElse: 'Something else',
    userOptedOut: 'User selected to opt out of Open Banking',
    overlayDismissed: 'Dismissed Open Banking',
    declinedTinkJourney: 'The user started the Open Banking journey but declined',
    unableToCompleteTinkJourney:
      'The user was unable to successfully complete the Open Banking journey',
  }

  return reasonMapping[category] || category
}

export const shouldShowTrustedSources = (
  income: Pick<Income, 'openBankingConnections' | 'konfirIntegration' | 'type'> &
    Partial<Pick<Income, 'status' | 'startDate'>>,
): boolean =>
  (income.openBankingConnections ?? []).length > 0 ||
  !!income.konfirIntegration ||
  isOpenBankingEligibleIncome(income)

export const getPayrollOptOutReasonText = (category: string): string => {
  const reasonMapping: Record<string, string> = {
    loginIssues: "I can't log in to my account",
    privacyConcerns: "I'm worried about my privacy",
    connectionTimeout: "It's not connecting or timed out",
    incorrectInformation: 'My information is wrong/missing',
    somethingElse: 'Something else',
    userOptedOut: 'User selected to opt out of Payroll',
  }

  return reasonMapping[category] || category
}
